import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useRouter } from 'next/router'
import { defaultGatewayStateConfig, OauthGateways, paymentGatewaysConfig } from 'src/assets/SettingsConfig'
import { GatewayStateConfigType, IPaymentGateways } from 'src/lib/types/settings'
// ** MUI Imports
import Typography from '@mui/material/Typography'
import SaveNotification from 'src/components/common/SaveNotification'
import { putRequest, postRequest } from 'src/lib/core/api-client'
import { apiURI, permissionType } from 'src/lib/utils/constants'
import Alerts from 'src/components/common/feedback-and-dialog/Alerts'
import { fetcher, staticOptions } from 'src/lib/core/api-client'
import useSWR, { mutate } from 'swr'
import { useLoader } from 'src/lib/contexts/LoaderContext'
import RowMenu from 'src/components/common/form-elements/RowMenu'
import FloGenericDialog from 'src/components/common/feedback-and-dialog/GenericDialog'
import { useUser } from 'src/lib/contexts/UserContext'
import { errorToast, successToast } from 'src/lib/utils/toasters'
import { useLocale } from 'src/lib/hooks/useLocal'
import FormTile from 'src/components/common/containers/FormTile'
import InputTextField from 'src/components/common/form-elements/InputTextField'
import { generateOauthLink } from 'src/lib/utils/settings'
import useGTM from 'src/lib/hooks/useGTM'
import { useMerchant } from 'src/lib/contexts/MerchantContext'

interface PaymentGatewaysProps {
  isOnboarding?: boolean
}

interface GatewayConfigProps {
  gatewayResponsesState: any[]
  gateway?: string
  isOnboarding?: boolean
  gatewayData: any
  setGatewayData: React.Dispatch<React.SetStateAction<any>>
  setGatewayConfigData: () => void
  initialGatewayData: GatewayStateConfigType | undefined
  merchant_id?: string
}
type MatchedGatewayUIConfigType = IPaymentGateways | undefined
const validateFields = (gatewayData: GatewayStateConfigType | undefined) => {
  const matchedGatewayUIConfig: MatchedGatewayUIConfigType = paymentGatewaysConfig.find(
    (pg: IPaymentGateways) => pg?.type === gatewayData?.pg
  )
  return matchedGatewayUIConfig?.inputConfigs?.every(inputConfig =>
    gatewayData?.[inputConfig?.valueType as keyof GatewayStateConfigType]
      ? gatewayData?.[inputConfig?.valueType as keyof GatewayStateConfigType]
      : undefined
  )
}
const PaymentGateways: React.FC<PaymentGatewaysProps> = ({ isOnboarding = false }) => {
  const router = useRouter()
  const { t } = useLocale()
  const { s_id } = router.query
  const [showPgSwitchDialog, setShowPgSwitchDialog] = useState<boolean>(false)
  const [switchParams, setSwitchParams] = useState<{ key: string; label: string }>({
    key: 'NONE',
    label: 'None'
  })

  const {
    getters: { merchant }
  } = useMerchant()

  const MID = merchant?.accountId ?? ''

  //For maintaning the state of configuration inputs
  const [gatewayData, setGatewayData] = useState<GatewayStateConfigType | undefined>(
    defaultGatewayStateConfig?.find((gateway: any) => {
      if (Boolean(s_id)) {
        return gateway.pg === s_id
      } else {
        return gateway.pg === gateway
      }
    })
  )

  const [initialGatewayData, setInitialGatewayData] = useState<GatewayStateConfigType | undefined>(
    defaultGatewayStateConfig?.find((gateway: any) => {
      if (Boolean(s_id)) {
        return gateway.pg === s_id
      } else {
        return gateway.pg === gateway
      }
    })
  )

  //For Onboarding
  const [openOnboardingDialog, setOpenOnboardingDialog] = useState<boolean>(false)
  const [gateway, setGateway] = useState<string>('')

  const { data: settingResponse, isValidating: isLoading } = useSWR(
    [`/merchant-attributes/settings?payments=true`, apiURI.FLO_KRATOS_CHECKOUT],
    ([url, baseURI]) => fetcher(url, baseURI),
    staticOptions
  )

  const [gatewayResponsesState, setGatewayResponsesState] = useState<any[]>()
  const { setLoaderProperties } = useLoader()
  const { canAccess } = useUser()

  useEffect(() => {
    if (!settingResponse) return
    const response = settingResponse as any
    const gatewaysResponse =
      (Boolean(response?.payment_details) && response?.payment_details?.data?.payments?.gateways) ?? []
    gatewaysResponse.sort((a: any, b: any) => b.is_configured - a.is_configured)
    gatewaysResponse.sort((a: any, b: any) => b.is_active - a.is_active)
    setGatewayResponsesState(gatewaysResponse)
  }, [settingResponse])

  console.log(gatewayResponsesState, 'gatewayResponsesState')

  const handleSwitchPg = async (key: string) => {
    if (!key) return

    try {
      setLoaderProperties({ isLoaderVisible: true, message: 'Updating payment gateway for payment methods' })
      await postRequest(`/fms/v1/pg-switch`, apiURI.FLO_CHECKOUT, { pg_name: key })
      mutate([`/merchant-attributes/settings?payments=true`, apiURI.FLO_KRATOS_CHECKOUT])
      mutate('/fms/v1/merchant-payment-modes')
    } catch (e) {
      console.error(e)
    } finally {
      setLoaderProperties({ isLoaderVisible: false })
    }
  }

  const setGatewayConfigData = () => {
    const selectedGateway = Boolean(s_id) ? s_id : gateway
    const matchedGatewayResponse: any = gatewayResponsesState?.find((gateway: any) => gateway.pg === selectedGateway)
    if (!matchedGatewayResponse) return
    const { metadata, fieldLabels, pg } = matchedGatewayResponse || {}
    const parsedData: GatewayStateConfigType = {
      pg: pg,
      pgMerchantId: metadata?.pg_config?.pg_merchant_id,
      pgKey: metadata?.pg_config?.key,
      pgSecret: metadata?.pg_config?.secret,
      ...(Boolean(metadata?.pg_config?.salt_index) ? { saltIndex: metadata?.pg_config?.salt_index } : {}),
      fieldLabels: fieldLabels,
      isConfigured: matchedGatewayResponse?.is_configured,
      isClickToActive: matchedGatewayResponse?.available_modes?.length > 1
    }
    setGatewayData(parsedData)
    setInitialGatewayData(parsedData)
  }

  const setSwitchParamsFn = (key: string, label: string) => {
    const hasMultiPgAccess = canAccess(permissionType.MULTI_PG_SUPPORT, 0)
    if (hasMultiPgAccess) return

    setShowPgSwitchDialog(true)
    setSwitchParams({
      key: key,
      label: label
    })
  }

  const renderGateways = useCallback(() => {
    return gatewayResponsesState?.map((gatewayResponse: any) => {
      const matchedGatewayUIConfig: any = paymentGatewaysConfig.find(
        (pg: IPaymentGateways) => pg.type === gatewayResponse.pg
      )
      if (!Boolean(matchedGatewayUIConfig)) return <></>

      const GatewayElement = () => (
        <GatewayTile
          type={matchedGatewayUIConfig?.type}
          imgPath={matchedGatewayUIConfig?.imgPath}
          label={matchedGatewayUIConfig?.label ?? 'Payment Gateway'}
          isActive={gatewayResponse?.is_active ?? false}
          isConfigured={gatewayResponse?.is_configured ?? false}
          isClickToActive={gatewayResponse?.available_modes?.length > 1}
          key={matchedGatewayUIConfig?.type}
          handleSwitchPg={() =>
            setSwitchParamsFn(matchedGatewayUIConfig?.type, matchedGatewayUIConfig?.label ?? 'Payment Gateway')
          }
        />
      )

      if (!Boolean(gatewayResponse.is_configured))
        return (
          <>
            {Boolean(isOnboarding) ? (
              <>
                {Boolean(gatewayResponse.configuration_url) ? (
                  <a
                    href={gatewayResponse.configuration_url}
                    rel='noreferrer'
                    key={matchedGatewayUIConfig?.type}
                    target='_blank'
                  >
                    <li key={gatewayResponse.pg} className='cursor-pointer'>
                      <GatewayElement />
                    </li>
                  </a>
                ) : (
                  <li
                    key={gatewayResponse.pg}
                    className='cursor-pointer'
                    onClick={() => {
                      setGateway(gatewayResponse?.pg)
                      setGatewayData(defaultGatewayStateConfig?.find((gateway: any) => gatewayResponse.pg === gateway))
                      setOpenOnboardingDialog(true)
                    }}
                  >
                    <GatewayElement />
                  </li>
                )}
              </>
            ) : (
              <li
                key={gatewayResponse.pg}
                className='cursor-pointer'
                onClick={async () => {
                  if (OauthGateways.includes(matchedGatewayUIConfig?.type)) {
                    await generateOauthLink(matchedGatewayUIConfig?.type, 'SETTING')
                    return
                  }

                  router.push({
                    pathname: '/settings/payments',
                    query: { s_id: matchedGatewayUIConfig?.type }
                  })
                }}
              >
                <GatewayElement />
              </li>
            )}
          </>
        )

      const rows = [
        {
          itemID: matchedGatewayUIConfig?.type,
          handleClick: async () => {
            if (OauthGateways.includes(matchedGatewayUIConfig?.type)) {
              await generateOauthLink(matchedGatewayUIConfig?.type, 'SETTING')
              return
            }
            router.push({
              pathname: '/settings/payments',
              query: { s_id: matchedGatewayUIConfig?.type }
            })
          },
          component: (
            <Typography variant='body2' sx={{ fontWeight: 500 }}>
              Edit
            </Typography>
          )
        }
      ]

      return (
        <li key={gatewayResponse.pg} className='relative'>
          <GatewayElement />
          <div className='absolute top-0 right-0 p-2'>
            <RowMenu menuData={rows} />
          </div>
        </li>
      )
    })
  }, [isOnboarding, router, gatewayResponsesState])

  const handleClick = async () => {
    const payload = {
      gateway_name: gateway,
      pg_merchant_id: gatewayData?.pgMerchantId,
      pg_config: {
        key: gatewayData?.pgKey,
        secret: gatewayData?.pgSecret
      }
    }

    if (!validateFields(gatewayData)) {
      errorToast(t('please_enter_all_the_fields'))
      return
    }

    try {
      setLoaderProperties({ isLoaderVisible: true, message: 'Updating payment gateway credentials' })
      await putRequest(`/md/merchant/${MID}/gateway`, apiURI.FLO_KRATOS_CHECKOUT, payload)
      mutate([`/merchant-attributes/settings?payments=true`, apiURI.FLO_KRATOS_CHECKOUT])
      successToast('Payment gateway credentials updated!')
    } catch (e: any) {
      console.error(e)
      errorToast(t('something_went_wrong'))
    } finally {
      setGateway('')
      setLoaderProperties({ isLoaderVisible: false })
      setOpenOnboardingDialog(false)
    }
  }

  if (Boolean(s_id)) {
    return (
      <GatewayConfig
        gatewayResponsesState={gatewayResponsesState ?? []}
        gatewayData={gatewayData}
        initialGatewayData={initialGatewayData}
        setGatewayData={setGatewayData}
        setGatewayConfigData={setGatewayConfigData}
        merchant_id={MID}
      />
    )
  }

  return (
    <>
      <ul className={`flex flex-row flex-wrap w-full gap-2  ${isOnboarding ? 'items-center justify-center ' : ''}`}>
        {renderGateways()}
      </ul>
      <FloGenericDialog
        isOpen={showPgSwitchDialog}
        width={600}
        handleClose={() => setShowPgSwitchDialog(false)}
        handleJob={async () => {
          handleSwitchPg(switchParams.key)
          setShowPgSwitchDialog(false)
        }}
        jobButtonText={`Switch all payment methods to ${switchParams.label}`}
        closeButtonText={'Cancel'}
        content={
          <p className='text-sm text-coal-dark'>
            Your current plan only allows 1 PG to be active at a time. Upgrade your plan to activate more PGs.
          </p>
        }
        title={`What do you want to do?`}
      />
      <FloGenericDialog
        isOpen={openOnboardingDialog}
        title={`Set up payment gateway`}
        handleClose={() => {
          //setGatewayData(defaultGatewayData)
          setOpenOnboardingDialog(false)
        }}
        content={
          <GatewayConfig
            initialGatewayData={initialGatewayData}
            gatewayResponsesState={gatewayResponsesState ?? []}
            gateway={gateway}
            isOnboarding={true}
            gatewayData={gatewayData}
            setGatewayData={setGatewayData}
            setGatewayConfigData={setGatewayConfigData}
            merchant_id={MID}
          />
        }
        jobButtonText={'Save'}
        handleJob={handleClick}
      />
    </>
  )
}

const GatewayConfig: React.FC<GatewayConfigProps> = React.memo(
  ({
    gatewayResponsesState,
    gateway,
    isOnboarding = false,
    gatewayData,
    setGatewayData,
    setGatewayConfigData,
    initialGatewayData,
    merchant_id
  }) => {
    const router = useRouter()
    const { t } = useLocale()
    const { s_id } = router.query
    const { setLoaderProperties } = useLoader()
    const { sendGTMTrigger } = useGTM()
    //For maintaining the UI inputs in the form
    const currentGatewayUIConfig = paymentGatewaysConfig?.find((gatewayConfig: any) => {
      if (Boolean(s_id)) {
        return gatewayConfig.type === s_id
      } else {
        return gatewayConfig.type === gateway
      }
    })

    const [showSaveStrip, setShowSaveStrip] = useState<boolean>(false)
    const [openAlert, setOpenAlert] = useState<string>('')

    useEffect(() => {
      if (!Boolean(gatewayResponsesState?.length) || Boolean(isOnboarding && !gateway)) return
      setGatewayConfigData()
    }, [s_id, gatewayResponsesState])

    const handleCloseAlert = (event: any, reason: any) => {
      if (reason === 'clickaway') {
        return
      }
      setOpenAlert('')
    }

    const handleUpdatePgCredentials = async () => {
      const matchedGatewayUIConfig: MatchedGatewayUIConfigType = paymentGatewaysConfig.find(
        (pg: IPaymentGateways) => pg?.type === gatewayData?.pg
      )

      const pg_config =
        matchedGatewayUIConfig?.inputConfigs?.reduce((acc, inputConfig) => {
          const value = gatewayData?.[inputConfig.valueType as keyof GatewayStateConfigType]
          if (inputConfig.payloadKey && Boolean(value)) {
            acc[inputConfig.payloadKey] = value as string
          }
          return acc
        }, {} as Record<string, string>) ?? {}
      const payload = {
        gateway_name: s_id,
        pg_config
      }

      if (!validateFields(gatewayData)) {
        errorToast(t('please_enter_all_the_fields'))
        return
      }
      try {
        setLoaderProperties({ isLoaderVisible: true, message: 'Updating payment gateway credentails' })
        const res = await putRequest(`/md/merchant/${merchant_id}/gateway`, apiURI.FLO_KRATOS_CHECKOUT, payload)
        if (res.error) {
          throw new Error('Invalid credentials')
        }
        sendGTMTrigger('payment_gateway_updated', 'edit', {
          gatewayName: gatewayData?.pg
        })
        setOpenAlert('success')
        setShowSaveStrip(false)
      } catch (e) {
        console.error(e)
        setOpenAlert('error')
      } finally {
        setLoaderProperties({ isLoaderVisible: false })
      }
    }

    const handleResetCredentials = useCallback(() => {
      setGatewayConfigData()
      setShowSaveStrip(false)
    }, [gatewayData])

    const handleFieldChange = (key: string, value: any) => {
      if (checkFieldsDisabled()) return
      setGatewayData((oldValues: any) => ({ ...oldValues, [key]: value }))
      setShowSaveStrip(true)
    }

    const checkFieldsDisabled = () => {
      switch (gatewayData?.pg) {
        case 'RP':
          return Boolean(gatewayData?.isConfigured)
        default:
          return false
      }
    }

    return (
      <>
        <FormTile title='Credentials'>
          <div className={`flex flex-col gap-3`}>
            {currentGatewayUIConfig?.inputConfigs?.map((inputConfig: any) => {
              return (
                <div key={inputConfig?.valueType} className={`flex ${inputConfig?.isHidden ? 'hidden' : ''}`}>
                  <InputTextField
                    textFieldId={inputConfig?.valueType}
                    label={inputConfig?.label}
                    inputPlaceholder='XXXXXXXXXXX'
                    inputValue={
                      gatewayData?.[inputConfig?.valueType as keyof GatewayStateConfigType] === undefined
                        ? ''
                        : gatewayData?.[inputConfig?.valueType as keyof GatewayStateConfigType]
                    }
                    isDisabled={checkFieldsDisabled()}
                    handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      handleFieldChange(inputConfig?.valueType, event.target.value)
                    }}
                    inputType={'text'}
                  />
                </div>
              )
            })}
          </div>
        </FormTile>
        {!isOnboarding && (
          <SaveNotification
            onSave={handleUpdatePgCredentials}
            onDiscard={handleResetCredentials}
            isVisible={showSaveStrip}
          />
        )}

        {openAlert.length ? (
          <Alerts
            open={true}
            duration={2000}
            closeHandler={handleCloseAlert}
            severity={openAlert}
            message={openAlert === 'success' ? 'Credentials updated!' : 'Invalid keys provided!'}
          />
        ) : null}
      </>
    )
  }
)

interface GatewayTileProps {
  type: string
  imgPath: string
  label: string
  isActive: boolean
  isConfigured: boolean
  isClickToActive: boolean
  handleSwitchPg: () => void
}

const GatewayTile: React.FC<GatewayTileProps> = React.memo(
  ({ imgPath, label, isActive, isConfigured, isClickToActive, handleSwitchPg }) => {
    const { canAccess } = useUser()
    const hasMultiPgAccess = canAccess(permissionType.MULTI_PG_SUPPORT, 0)

    return (
      <div
        className={`w-[9rem] h-32 border  rounded-lg p-3 flex flex-col items-center ${
          isActive && isConfigured ? 'bg-[#01cca70d] border-[#01cca71a]' : 'bg-white border-[#F0F0F0]'
        }`}
      >
        <img src={imgPath} alt={label} className='border rounded h-9 w-9' />
        <p className='mt-2 text-sm font-medium text-coal-dark'>{label}</p>
        <div className='mt-4'>
          {Boolean(isActive) && Boolean(isConfigured) ? (
            <p className='bg-[#01cca71a] px-2 text-primary-dark rounded-xl text-xs py-0.5 '>Active</p>
          ) : (
            <button
              className='px-2 text-xs rounded-xl bg-gray-lighter text-gray-dark py-0.5 cursor-pointer'
              onClick={handleSwitchPg}
            >
              {Boolean(isConfigured)
                ? Boolean(hasMultiPgAccess) && Boolean(isClickToActive)
                  ? 'Assign to method'
                  : 'Click to activate'
                : 'Click to setup'}{' '}
            </button>
          )}
        </div>
      </div>
    )
  }
)

export default PaymentGateways
